import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { setRememberedAccount } from 'src/lib/rememberedAccount';
import { UserService } from '../UserService';

export const useLoginMutation = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (credentials) => {
      const response = await UserService.loginUser(credentials);
      return response.data;
    },
    onSuccess: (data) => {
      const { tokens } = data;

      if (tokens?.access_token) {
        localStorage.setItem('token', tokens.access_token);
        if (tokens.refresh_token) {
          localStorage.setItem('refreshToken', tokens.refresh_token);
        }

        // Decode JWT token to extract user information
        try {
          const decodedToken = jwtDecode(tokens.access_token);

          // Format user data from decoded token
          const userData = {
            cid: decodedToken.cid || decodedToken.sub,
            email: decodedToken.email,
            firstName: decodedToken.given_name,
            lastName: decodedToken.family_name,
            username: decodedToken.preferred_username,
            emailVerified: decodedToken.email_verified || false,
            dbSyncStatus: decodedToken.db_sync || null,
            identityProvider: decodedToken.identity_provider || null,
            roles: decodedToken.realm_access?.roles || [],
          };

          // Store user data
          localStorage.setItem('user', JSON.stringify(userData));
          // Powers the login screen's "welcome back" card on this browser's
          // next visit — separate from the `user` key above, which logout
          // clears (see rememberedAccount.js for why).
          setRememberedAccount({
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
          });

          // The index route (LandingPage, router.jsx) is what actually
          // decides where a given role lands — plain '/' rather than a
          // hardcoded '/opportunities' here, so a role-specific landing
          // page (e.g. admin-only -> /personas, interviewer-only ->
          // /interviews/upcoming) is honored right after login too, not
          // just on a page refresh.
          navigate('/');
        } catch (error) {
          console.error('Error decoding JWT token:', error);
          throw new Error('Failed to decode authentication token');
        }
      }
    },
    onError: (error) => {
      if (error.response) {
        switch (error.response.status) {
          case 401:
            throw new Error('Invalid credentials');
          case 403:
            throw new Error('Account is locked. Please contact support');
          case 404:
            throw new Error('Account not found');
          case 422:
            throw new Error('Invalid login credentials');
          case 429:
            throw new Error('Too many login attempts. Please try again later');
          default:
            throw new Error(
              error.response.data?.message || 'An error occurred during login'
            );
        }
      }
    },
  });
};

export const useAhedgeUsersQuery = (role, pageNo = 1, pageSize = 10) =>
  useQuery({
    queryKey: ['ahedge-users', role, pageNo, pageSize],
    queryFn: async () => {
      const response = await UserService.getAhedgeUsers(role, pageNo, pageSize);
      return response.data;
    },
    enabled: !!role,
  });

export const useLogoutMutation = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        const logoutData = {
          refresh_token: refreshToken,
        };
        await UserService.logoutUser(logoutData);
      }
    },
    onSuccess: () => {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      navigate('/login');
    },
    onError: (error) => {
      console.error('Logout failed:', error);
      // Still clear local storage and redirect on error
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      navigate('/login');
    },
  });
};

export const useChangePasswordMutation = () =>
  useMutation({
    mutationFn: async (passwordData) => {
      const response = await UserService.changePassword(passwordData);
      return response.data;
    },
  });
